/**
 * NOVO CASO e EDITAR CASO — em passo a passo desde 18/09/2026.
 *
 * O quarto formulário da fila (`docs/FORMULARIOS-E-LINK-DO-CLIENTE.md`, A.4),
 * depois do cadastro pelo link, do cliente e da proposta, com as mesmas peças
 * (`PassoAPasso.tsx`).
 *
 * ⛔ **Muda o desenho, não o que o botão faz.** Continua igual:
 *
 * - **Nada é obrigatório, fora os limites do Customizado** (investigados e prazo
 *   em dias) — o Customizado não tem número de contrato padrão.
 * - **O prazo sai da assinatura** e para de recalcular quando escrito à mão;
 *   apagar a data escrita devolve o cálculo.
 * - **Caso sem assinatura é pendência**, nunca caso sem prazo.
 * - **Gravar pergunta antes** e **fechar com alteração pendente avisa**.
 * - O que se grava é o mesmo, pela mesma rota.
 *
 * ⚠️ **O colaborador também abre caso** ("vê e cadastra"): nada aqui mostra
 * dinheiro. Editar é do master, como sempre.
 *
 * ⭐⭐ **O caso nasce com o que o pedido trouxe — 29/10/2026** (PRD §74): o
 * último passo mostra o que vem do pedido de pesquisa (a dívida, os
 * investigados, o que o cliente informou) e, quando há mais de uma pesquisa
 * possível, pergunta de qual o caso é. Quem decide é o servidor; a tela mostra.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { GuardaDoFormulario } from './GuardaDoFormulario.tsx';
import { Icone } from './Icone.tsx';
import { AjudaDoPasso, CampoDoPasso, EscolhaDoNivel, PassosDoTopo, Progresso, ResumoDoPasso } from './PassoAPasso.tsx';
import { usarFormulario } from '../lib/formulario.ts';
import { maiuscula } from '../lib/caixa.ts';
import { usarDialogo } from '../lib/dialogo.ts';
import { apiCasos, apiInvestigados, ErroDaApi, type Caso, type Contrato, type DadosCaso, type NivelServico, type OrigemDoCasoNovo } from '../lib/api.ts';
import { usarEhMaster } from '../lib/papel.ts';
import { paraTela } from '../lib/datas.ts';
import { NIVEIS, SITUACOES_DO_CONTRATO } from '../lib/rotulos.ts';
import '../estilos/caso-2026.css';

/** Os mesmos números do servidor (`api/src/lib/niveis.ts`), só para prever o
 *  prazo e o aviso enquanto se digita. Quem decide é sempre o servidor. */
const LIMITES: Record<NivelServico, { investigados: number | null; dias: number | null; reunioes: number | null }> = {
  n1_insight: { investigados: 5, dias: 30, reunioes: 1 },
  n2_forensic: { investigados: 12, dias: 60, reunioes: 3 },
  n3_kairos: { investigados: 22, dias: 90, reunioes: 4 },
  customizado: { investigados: null, dias: null, reunioes: null },
};

function somarDias(data: string, dias: number): string {
  const d = new Date(`${data}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
}

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

const diaAno = (iso: string) =>
  new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(iso));

type OpcaoDoPedido = OrigemDoCasoNovo['opcoes'][number];
const chaveDaOpcao = (o: { pedidoId: string; pesquisa: number }) => `${o.pedidoId}:${o.pesquisa}`;

/** O que a pesquisa traz, numa linha: os marcos preenchidos e a nota do cliente. */
function oQueVem(o: OpcaoDoPedido): string {
  const partes = [plural(o.quantos, 'investigado', 'investigados')];
  if (o.marcos.length) partes.push(`a dívida (${o.marcos.map((m) => m.toLowerCase()).join(', ')})`);
  if (o.notas) partes.push('o que o cliente informou');
  return partes.join(' · ');
}

type Props = {
  clienteId: string;
  clienteNome: string;
  contratos: Contrato[];
  inicial: Caso | null;
  aoFechar: () => void;
  aoSalvar: (dados: DadosCaso) => Promise<void>;
};

export function FormularioCaso({ clienteId, clienteNome, contratos, inicial, aoFechar, aoSalvar }: Props) {
  const editando = inicial !== null;
  const [contratoId, setContratoId] = useState(inicial?.contrato?.id ?? '');
  const [titulo, setTitulo] = useState(inicial?.titulo ?? '');
  const [nivel, setNivel] = useState<NivelServico>(inicial?.nivel ?? 'n1_insight');
  /*
   * ⚠️ **O campo "Investigados neste caso" SAIU — 20/08/2026.** *"a quantidade
   * será determinada automaticamente quando eu cadastrar os investigados"* — a
   * contagem é a ficha. A coluna continua no banco com o default `1`, e a rota
   * ainda a aceita: o número segue indo como sempre foi.
   */
  const investigados = String(inicial?.investigados ?? 1);
  const [assinadoEm, setAssinadoEm] = useState(inicial?.assinadoEm ?? '');
  const [prazoEm, setPrazoEm] = useState(inicial?.prazoManual ? (inicial.prazoEm ?? '') : '');
  const [observacao, setObservacao] = useState(inicial?.observacao ?? '');
  /* ⭐ §77: a chave do caso que saiu da ficha do imóvel (decisão dele, 30/10/2026); só o master a vê, ao editar o caso. */
  const ehMaster = usarEhMaster();
  const [soNoHub, setSoNoHub] = useState(inicial?.leituraSoNoHub ?? false);

  /* ⭐ O que o caso traz do pedido (29/10/2026). `'nenhum'` é o "não trazer"
     escolhido; `pedidoId:pesquisa`, a pesquisa escolhida. */
  const [origem, setOrigem] = useState<OrigemDoCasoNovo | null>(null);
  const [escolhaDoPedido, setEscolhaDoPedido] = useState<string | null>(null);
  const [versaoDaOrigem, setVersaoDaOrigem] = useState(0);

  const [limInv, setLimInv] = useState(String(inicial?.limites.investigados ?? ''));
  const [limDias, setLimDias] = useState(String(inicial?.limites.dias ?? ''));
  const [limReu, setLimReu] = useState(String(inicial?.limites.reunioes ?? ''));

  const passos = ['O caso', 'Nível', 'Prazo', editando ? 'Conferir e salvar' : 'Conferir e gravar'];
  const ULTIMO = passos.length - 1;
  const [passo, setPasso] = useState(0);
  const [alcancado, setAlcancado] = useState(editando ? ULTIMO : 0);

  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const emVoo = useRef(false);

  const guarda = usarFormulario({ contratoId, titulo, nivel, investigados, assinadoEm, prazoEm, observacao, limInv, limDias, limReu, soNoHub }, aoFechar);
  usarDialogo(guarda.fechar);

  // O contrato e o nível decidem a pesquisa: pergunta de novo a cada troca.
  useEffect(() => {
    if (editando) return;
    let vivo = true;
    apiCasos
      .origemDoPedido({ clienteId, contratoId: contratoId || null, nivel })
      .then((o) => vivo && setOrigem(o))
      .catch(() => vivo && setOrigem(null));
    return () => {
      vivo = false;
    };
  }, [editando, clienteId, contratoId, nivel, versaoDaOrigem]);

  const vemSozinha = origem?.automatica ? (origem.opcoes.find((o) => chaveDaOpcao(o) === chaveDaOpcao(origem.automatica!)) ?? null) : null;
  const perguntar = !editando && !!origem?.precisaEscolher;
  const escolhida = perguntar ? (origem!.opcoes.find((o) => chaveDaOpcao(o) === escolhaDoPedido) ?? null) : null;
  const naoTrazer = perguntar && escolhaDoPedido === 'nenhum';
  /** O que vai ao servidor: o que a tela mostrou. Sem resposta da prévia, o servidor decide. */
  const doPedido: DadosCaso['doPedido'] = editando
    ? undefined
    : vemSozinha
      ? origem!.automatica!
      : escolhida
        ? { pedidoId: escolhida.pedidoId, pesquisa: escolhida.pesquisa }
        : naoTrazer
          ? 'nenhum'
          : undefined;

  const customizado = nivel === 'customizado';
  const dias = customizado ? Number(limDias) || null : LIMITES[nivel].dias;
  const teto = customizado ? Number(limInv) || null : LIMITES[nivel].investigados;
  const reunioes = customizado ? Number(limReu) || 0 : LIMITES[nivel].reunioes;

  const prazoPrevisto = useMemo(() => {
    if (prazoEm) return null;
    if (!assinadoEm || !dias) return null;
    return somarDias(assinadoEm, dias);
  }, [assinadoEm, dias, prazoEm]);

  /** Mexeu, o aviso de erro sai: aviso velho manda corrigir o que já está certo. */
  const mexe =
    <T,>(f: (v: T) => void) =>
    (v: T) => {
      setErro(null);
      f(v);
    };

  /** Só o Customizado tem obrigatório: os números dele são do briefing, não da tabela. */
  function problemaDoPasso(i: number): string | null {
    if (i === 1 && customizado && (!Number(limInv) || !Number(limDias))) {
      return 'No Customizado, informe o limite de investigados e o prazo em dias.';
    }
    if (i === ULTIMO && perguntar && !escolhida && !naoTrazer) {
      return 'Escolha de qual pesquisa do pedido é este caso, ou "Não trazer nada do pedido".';
    }
    return null;
  }

  function irPara(p: number) {
    setErro(null);
    setPasso(p);
  }
  function avancar() {
    const x = problemaDoPasso(passo);
    if (x) return setErro(x);
    setAlcancado((a) => Math.max(a, passo + 1));
    irPara(passo + 1);
  }
  function pedirParaGravar() {
    if (emVoo.current) return;
    for (const i of [1, ULTIMO]) {
      const x = problemaDoPasso(i);
      if (x) {
        setPasso(i);
        setAlcancado((a) => Math.max(a, i));
        return setErro(x);
      }
    }
    // A gravação só acontece depois do "sim". Ver lib/formulario.ts.
    guarda.pedirParaSalvar();
  }

  async function gravar() {
    if (emVoo.current) return;
    guarda.cancelarPendencia();
    emVoo.current = true;
    setSalvando(true);
    setErro(null);
    try {
      if (editando && ehMaster && soNoHub !== (inicial.leituraSoNoHub ?? false)) await apiInvestigados.leituraSoNoHub(inicial.id, soNoHub);
      await aoSalvar({
        clienteId,
        contratoId: contratoId || null,
        titulo: titulo.trim() || null,
        nivel,
        investigados: Number(investigados),
        assinadoEm: assinadoEm || null,
        prazoEm: prazoEm || null,
        observacao: observacao.trim() || null,
        limiteInvestigados: customizado ? Number(limInv) : null,
        limitePrazoDias: customizado ? Number(limDias) : null,
        limiteReunioes: customizado ? Number(limReu) || 0 : null,
        ...(doPedido !== undefined && { doPedido }),
      });
    } catch (e) {
      // O pedido mudou entre a prévia e a gravação: a prévia volta a perguntar.
      if (e instanceof ErroDaApi && e.status === 409 && !editando) {
        setVersaoDaOrigem((v) => v + 1);
        setPasso(ULTIMO);
      }
      setErro(e instanceof Error ? e.message : 'Não consegui salvar.');
      emVoo.current = false;
      setSalvando(false);
    }
  }

  const contrato = contratos.find((c) => c.id === contratoId) ?? null;
  const entrega = prazoEm ? `${paraTela(prazoEm)} · escrita à mão` : prazoPrevisto ? `${paraTela(prazoPrevisto)} · ${dias} dias da assinatura` : '';
  const ajuda = passo < ULTIMO ? AJUDA[passo] : undefined;

  return (
    <div className="cortina cad-cortina" onMouseDown={(e) => e.target === e.currentTarget && guarda.fechar()}>
      <div className="imv cad-conferir pap-ficha cso-ficha" role="dialog" aria-modal="true" aria-label={editando ? 'Editar caso' : 'Novo caso'}>
        <section className="imv-ficha">
          <header className="imv-ficha-topo">
            <div>
              <span className="s-sobrelinha">
                Ficha do cliente · {editando ? 'editar caso' : 'novo caso'} · passo {passo + 1} de {passos.length}
              </span>
              <h3 className="cad-titulo">
                {editando ? (
                  <>
                    Editar caso <span className="pap-titulo-codigo">{inicial.codigo}</span>
                  </>
                ) : (
                  'Novo caso'
                )}
              </h3>
              <p className="cad-sub">
                <b>{clienteNome}</b> ·{' '}
                {editando
                  ? 'Todos os passos estão abertos: mude o que precisar e salve de qualquer um deles.'
                  : 'O caso nasce em Contratado e recebe o código do ano. Nada aqui é obrigatório, fora os limites do Customizado.'}
              </p>
            </div>
            <button type="button" className="imv-fechar" aria-label="Fechar" onClick={guarda.fechar}>
              <Icone nome="fechar" tamanho={16} />
            </button>
          </header>
          <PassosDoTopo passos={passos} atual={passo} alcancado={alcancado} aoIr={irPara} />

          <div className="imv-corpo">
            <div className="imv-conteudo">
              {passo === 0 && (
                <>
                  <div className="imv-grade-campos">
                    <CampoDoPasso rotulo="Título" col={4} nota='O código identifica; o título é o que responde "qual era aquele caso?".'>
                      <input
                        id="tcaso"
                        value={titulo}
                        onChange={(e) => mexe(setTitulo)(maiuscula(e.target.value))}
                        placeholder="EX.: GRUPO ECONÔMICO SILVA"
                        maxLength={200}
                        autoFocus={!editando}
                      />
                    </CampoDoPasso>
                  </div>

                  {/* Um contrato pode cobrir vários casos. A linha diz o que ele
                      vendeu (os selos) e em que pé está — nada de valor: o
                      colaborador também abre esta janela. */}
                  {contratos.length > 0 && (
                    <div className="cso-contratos">
                      <span className="imv-rotulo">Contrato</span>
                      <div className="pap-marcaveis" role="radiogroup" aria-label="Contrato do caso">
                        <label className={`pap-marcavel${!contratoId ? ' on' : ''}`}>
                          <input type="radio" name="contrato-do-caso" checked={!contratoId} onChange={() => mexe(setContratoId)('')} />
                          <span className="pap-marcavel-texto">Sem contrato vinculado</span>
                        </label>
                        {contratos.map((c) => (
                          <label key={c.id} className={`pap-marcavel${contratoId === c.id ? ' on' : ''}`}>
                            <input type="radio" name="contrato-do-caso" checked={contratoId === c.id} onChange={() => mexe(setContratoId)(c.id)} />
                            <span className="pap-codigo">{c.codigo}</span>
                            <span className={`etiqueta etiqueta-${c.situacao}`}>{SITUACOES_DO_CONTRATO[c.situacao]}</span>
                            <span className="pap-marcavel-texto">
                              {c.niveisDoContrato.map((n) => (
                                <span key={n.nivel} className={`selo-nivel selo-${n.nivel}`}>
                                  {NIVEIS[n.nivel].longo}
                                </span>
                              ))}
                            </span>
                            <span className="pap-marcavel-nota fraca">{c.casos === 0 ? 'sem caso ainda' : `cobre ${plural(c.casos, 'caso', 'casos')}`}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="imv-grade-campos cso-obs">
                    <CampoDoPasso rotulo="Observação" col={6} nota="Fica só no Hub. O que o cliente contou no pedido vem junto com o caso, no fim desta observação.">
                      <textarea id="obsc" rows={3} value={observacao} onChange={(e) => mexe(setObservacao)(e.target.value)} maxLength={4000} />
                    </CampoDoPasso>
                  </div>
                  {editando && ehMaster && (
                    <label className="leitura-so-no-hub">
                      <input type="checkbox" checked={soNoHub} onChange={(e) => mexe(setSoNoHub)(e.target.checked)} /> Este caso não vai a IA de fora{' '}
                      <span className="ajuda">
                        (as matrículas dele são lidas só pelo Hub, e nada sai). Marque só quando o contrato com o cliente proibir IA
                        fora do Brasil ou o cliente pedir sigilo reforçado.
                      </span>
                    </label>
                  )}
                </>
              )}

              {passo === 1 && (
                <div className={`pap-${nivel}`}>
                  <EscolhaDoNivel nivel={nivel} rotulo="Nível do caso" aoEscolher={mexe(setNivel)} />
                  <p className="pap-clausula">
                    {customizado ? (
                      <>Escopo definido após briefing: os limites abaixo são os que se combinaram.</>
                    ) : (
                      <>
                        <b>{NIVEIS[nivel].verbo}</b> · até {plural(LIMITES[nivel].investigados!, 'investigado', 'investigados')} · {LIMITES[nivel].dias} dias ·{' '}
                        {plural(LIMITES[nivel].reunioes!, 'reunião', 'reuniões')}
                      </>
                    )}
                  </p>
                  {customizado && (
                    <div className="imv-grade-campos">
                      <CampoDoPasso rotulo="Investigados" obrigatorio col={2} mono nota="O teto combinado.">
                        <input id="li" inputMode="numeric" value={limInv} onChange={(e) => mexe(setLimInv)(e.target.value.replace(/\D/g, '').slice(0, 3))} />
                      </CampoDoPasso>
                      <CampoDoPasso rotulo="Prazo" obrigatorio col={2} mono nota="Contado da assinatura.">
                        <input id="ld" inputMode="numeric" value={limDias} onChange={(e) => mexe(setLimDias)(e.target.value.replace(/\D/g, '').slice(0, 4))} />
                        <i className="imv-suf">dias</i>
                      </CampoDoPasso>
                      <CampoDoPasso rotulo="Reuniões" col={2} mono nota="Em branco, nenhuma.">
                        <input id="lr" inputMode="numeric" value={limReu} onChange={(e) => mexe(setLimReu)(e.target.value.replace(/\D/g, '').slice(0, 3))} />
                      </CampoDoPasso>
                    </div>
                  )}
                  {editando && inicial.nivel !== nivel && (
                    <p className="imv-pendencias">
                      <b>Trocar o nível</b> muda o teto, as reuniões e o prazo (se não foi escrito à mão), e o relatório passa a ter os tópicos de{' '}
                      {NIVEIS[nivel].longo} ao ser aberto. <b>Texto já escrito nunca se apaga.</b>
                    </p>
                  )}
                </div>
              )}

              {passo === 2 && (
                <>
                  <div className="imv-grade-campos">
                    <CampoDoPasso rotulo="Assinatura do contrato" col={2} mono nota={assinadoEm ? 'O prazo conta daqui.' : undefined}>
                      <input id="assinado" type="date" value={assinadoEm} onChange={(e) => mexe(setAssinadoEm)(e.target.value)} />
                    </CampoDoPasso>
                    <CampoDoPasso rotulo="Entrega" col={2} mono nota={prazoEm ? 'Escrita à mão: para de recalcular. Apague a data para voltar a calcular.' : 'Em branco, calcula da assinatura.'}>
                      <input id="prazo" type="date" value={prazoEm} onChange={(e) => mexe(setPrazoEm)(e.target.value)} />
                    </CampoDoPasso>
                  </div>
                  {/* ⚠️ A conta daqui é a crua (assinatura + dias). Quem antecipa
                      para o dia útil anterior é o servidor, que conhece os
                      feriados — por isso a frase diz que ele antecipa. */}
                  {prazoEm ? (
                    <div className="cso-prazo">
                      <span className="imv-rotulo">Entrega</span>
                      <strong>{paraTela(prazoEm)}</strong>
                      <small>escrita à mão — o Hub não recalcula</small>
                    </div>
                  ) : prazoPrevisto ? (
                    <div className="cso-prazo">
                      <span className="imv-rotulo">Entrega prevista</span>
                      <strong>{paraTela(prazoPrevisto)}</strong>
                      <small>
                        {dias} dias da assinatura; se cair em fim de semana ou feriado, o Hub antecipa para o dia útil anterior
                      </small>
                    </div>
                  ) : (
                    <p className="imv-pendencias">
                      <b>Sem a assinatura, o prazo não começa a contar</b> — o caso aparece como pendência até ela entrar
                      {customizado && !dias ? ', e o Customizado precisa do prazo em dias' : ''}.
                    </p>
                  )}
                </>
              )}

              {passo === ULTIMO && (
                <div className="cad-resumo">
                  <ResumoDoPasso
                    titulo="O caso"
                    aoCorrigir={() => irPara(0)}
                    linhas={[
                      ['Título', titulo.trim()],
                      [
                        'Contrato',
                        contrato ? (
                          <>
                            <span className="pap-codigo">{contrato.codigo}</span> · {SITUACOES_DO_CONTRATO[contrato.situacao].toLowerCase()}
                          </>
                        ) : contratos.length > 0 ? (
                          'sem contrato vinculado'
                        ) : (
                          ''
                        ),
                      ],
                    ]}
                  />
                  <ResumoDoPasso
                    titulo="Nível"
                    aoCorrigir={() => irPara(1)}
                    linhas={[
                      ['Nível', <span className={`selo-nivel selo-${nivel}`}>{NIVEIS[nivel].longo}</span>],
                      ['Investigados', teto ? `até ${teto}` : ''],
                      ['Prazo', dias ? `${dias} dias` : ''],
                      ['Reuniões', reunioes !== null ? String(reunioes) : ''],
                    ]}
                  />
                  <ResumoDoPasso
                    titulo="Prazo"
                    aoCorrigir={() => irPara(2)}
                    linhas={[
                      ['Assinatura', assinadoEm ? paraTela(assinadoEm) : ''],
                      ['Entrega', entrega],
                    ]}
                  />
                  <ResumoDoPasso
                    titulo="Observação"
                    aoCorrigir={() => irPara(0)}
                    linhas={[['Observação', observacao.trim()], ...(editando && ehMaster && soNoHub ? ([['IA de fora', 'este caso não vai (só o Hub lê as matrículas)']] as [string, string][]) : [])]}
                  />
                  {!editando && vemSozinha && <VemDoPedido opcao={vemSozinha} />}
                  {perguntar && (
                    <EscolhaDoPedido opcoes={origem!.opcoes} escolha={escolhaDoPedido} aoEscolher={mexe(setEscolhaDoPedido)} />
                  )}
                </div>
              )}
            </div>
            {ajuda && <AjudaDoPasso titulo={ajuda.titulo}>{ajuda.corpo(editando)}</AjudaDoPasso>}
          </div>

          {erro && <p className="erro imv-erro">{erro}</p>}

          <footer className="imv-ficha-pe">
            <button type="button" className="imv-bt fantasma" onClick={guarda.fechar}>
              Cancelar
            </button>
            <span className="imv-esp" />
            {passo > 0 && (
              <button type="button" className="imv-bt fantasma" onClick={() => irPara(passo - 1)}>
                <Icone nome="voltar" tamanho={14} /> Voltar
              </button>
            )}
            <Progresso atual={passo} total={passos.length} />
            {passo < ULTIMO && (
              <button type="button" className={`imv-bt ${editando ? 'fantasma' : 'primario'}`} onClick={avancar}>
                Avançar <Icone nome="avancar" tamanho={14} />
              </button>
            )}
            {(editando || passo === ULTIMO) && (
              <button type="button" className="imv-bt primario" disabled={salvando} onClick={pedirParaGravar}>
                <Icone nome="check" tamanho={14} /> {salvando ? 'Salvando…' : editando ? 'Salvar alterações' : 'Gravar caso'}
              </button>
            )}
          </footer>
        </section>
      </div>

      <GuardaDoFormulario
        pendencia={guarda.pendencia}
        oQue={editando ? `o caso ${inicial.codigo}` : 'o caso'}
        aoCancelar={guarda.cancelarPendencia}
        aoSalvar={() => void gravar()}
        aoDescartar={guarda.aoFecharDeVerdade}
      />
    </div>
  );
}

/**
 * ⭐ O que vem do pedido, quando é uma pesquisa só (29/10/2026). Mostra o nome
 * dos investigados: é a ficha do cliente, onde o pedido já os mostra.
 */
function VemDoPedido({ opcao }: { opcao: OpcaoDoPedido }) {
  return (
    <section className="cad-bloco cad-resumo-inteiro cso-pedido" aria-label="Vem do pedido de pesquisa">
      <header>
        <h6>Vem do pedido de pesquisa</h6>
      </header>
      <dl>
        <div>
          <dt>Pedido</dt>
          <dd>
            {diaAno(opcao.recebidoEm)} · pesquisa {opcao.pesquisa + 1} · <span className={`selo-nivel selo-${opcao.nivel}`}>{NIVEIS[opcao.nivel].longo}</span>
          </dd>
        </div>
        <div>
          <dt>Investigados</dt>
          <dd>{opcao.investigados.length ? opcao.investigados.join(' · ') : plural(opcao.quantos, 'investigado', 'investigados')}</dd>
        </div>
        <div>
          <dt>Dívida</dt>
          <dd className={opcao.marcos.length ? undefined : 'falta'}>{opcao.marcos.length ? opcao.marcos.join(' · ') : 'o cliente não preencheu'}</dd>
        </div>
        {opcao.notas && (
          <div>
            <dt>O que o cliente informou</dt>
            <dd>vai para o fim da observação do caso</dd>
          </div>
        )}
      </dl>
      <p className="cso-pedido-nota">Entra junto com o caso, na mesma gravação. O pedido continua guardado como chegou.</p>
    </section>
  );
}

/** ⭐ Mais de uma pesquisa possível: quem cria o caso diz de qual ele é — ou que não traz nada. */
function EscolhaDoPedido({ opcoes, escolha, aoEscolher }: { opcoes: OpcaoDoPedido[]; escolha: string | null; aoEscolher: (v: string) => void }) {
  return (
    <section className="cad-bloco cad-resumo-inteiro cso-pedido" aria-label="De qual pesquisa é este caso">
      <header>
        <h6>De qual pesquisa do pedido é este caso?</h6>
      </header>
      <p className="cso-pedido-nota">Este cliente tem pedido de pesquisa que ainda não está em caso nenhum. O que se escolher aqui entra junto com o caso.</p>
      <div className="pap-marcaveis" role="radiogroup" aria-label="Pesquisa do pedido">
        {opcoes.map((o) => (
          <label key={chaveDaOpcao(o)} className={`pap-marcavel${escolha === chaveDaOpcao(o) ? ' on' : ''}`}>
            <input type="radio" name="pesquisa-do-pedido" checked={escolha === chaveDaOpcao(o)} onChange={() => aoEscolher(chaveDaOpcao(o))} />
            <span className="pap-codigo">{diaAno(o.recebidoEm)}</span>
            <span className={`selo-nivel selo-${o.nivel}`}>{NIVEIS[o.nivel].longo}</span>
            <span className="pap-marcavel-texto">
              pesquisa {o.pesquisa + 1}
              {o.doContrato ? ' · do contrato escolhido' : ''}
              {o.investigados.length ? ` · ${o.investigados.join(' · ')}` : ''}
            </span>
            <span className="pap-marcavel-nota fraca">{oQueVem(o)}</span>
          </label>
        ))}
        <label className={`pap-marcavel${escolha === 'nenhum' ? ' on' : ''}`}>
          <input type="radio" name="pesquisa-do-pedido" checked={escolha === 'nenhum'} onChange={() => aoEscolher('nenhum')} />
          <span className="pap-marcavel-texto">Não trazer nada do pedido</span>
          <span className="pap-marcavel-nota fraca">o caso nasce vazio; o pedido fica guardado e o Trazer do pedido continua no cartão</span>
        </label>
      </div>
    </section>
  );
}

/** A coluna da direita, passo a passo. Texto de tela: sem fonte, sem ferramenta. */
const AJUDA: Array<{ titulo: string; corpo: (editando: boolean) => ReactNode }> = [
  {
    titulo: 'O caso',
    corpo: () => (
      <>
        <p>O caso é o trabalho: nível, investigados, prazo e reuniões moram nele, não no cliente.</p>
        <p>
          <b>Um contrato pode cobrir vários casos</b> — pacote ou avença. Sem contrato agora, dá para vincular depois, no contrato.
        </p>
        <p>
          <b>Contrato que veio de um pedido de pesquisa</b> traz junto o que o cliente preencheu: a dívida, os investigados e o que ele
          informou. O último passo mostra o que vem.
        </p>
      </>
    ),
  },
  {
    titulo: 'Nível',
    corpo: () => (
      <>
        <p>O nível decide o teto de investigados, o prazo, as reuniões e os tópicos do relatório.</p>
        <p>
          O teto <b>avisa, não bloqueia</b>: a realidade do caso manda, e o aditivo se formaliza depois.
        </p>
      </>
    ),
  },
  {
    titulo: 'Prazo',
    corpo: () => (
      <>
        <p>
          O prazo conta da <b>assinatura do contrato</b>. A entrega vai sozinha ao calendário.
        </p>
        <p>Escrever a entrega à mão para o cálculo; apagar a data devolve.</p>
      </>
    ),
  },
];
